import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../api/client';
import '../kits/KitPopup.css';
import './WildmicrPopup.css';
import './SetPopup.css';
import './IndividualPacketPopup.css';
import MarketplacesField, { ALL_MARKETPLACES } from './MarketplacesField';
import AlternateIdsField from './AlternateIdsField';
import ProductDetailsField from './ProductDetailsField';

const IndividualPacketPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    const [form, setForm] = useState({
        SKU: '', packName: '', ASIN: '', FNSKU: '', GTIN: '', modelNumber: '', brandID: '',
        bagsPerBox: '', gperpacket: '', seedsperpacket: '',
        indSale: 'NO', niche_category_id: '', seedsID: '', marketplaces: ALL_MARKETPLACES,
    });
    const [brands,   setBrands]   = useState([]);
    const [niches,   setNiches]   = useState([]);
    const [allSeeds,     setAllSeeds]     = useState([]);
    const [selectedSeed, setSelectedSeed] = useState(null); // { seedsID, label }
    const [seedSearch,   setSeedSearch]   = useState('');
    const [seedDropOpen, setSeedDropOpen] = useState(false);
    const seedWrapRef  = useRef(null);
    const seedInputRef = useRef(null);
    const [dropStyle, setDropStyle] = useState({});
    const [usedInSets, setUsedInSets] = useState([]);  // [{SKU, setmane, brandName}]
    const [loadingSets, setLoadingSets] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    useEffect(() => {
        if (!isOpen) return;
        api.get('/api/options/brands').then(r => setBrands(r.data.items || [])).catch(() => {});
        api.get('/api/options/niche_category').then(r => setNiches(r.data.items || [])).catch(() => {});
        api.get('/api/options/seeds').then(r => setAllSeeds(r.data.items || [])).catch(() => {});
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setForm({
                SKU:              editData.SKU              || '',
                packName:         editData.packName         || '',
                ASIN:             editData.ASIN             || '',
                FNSKU:            editData.FNSKU            || '',
                GTIN:             editData.GTIN             || '',
                modelNumber:      editData.modelNumber      ?? '',
                brandID:         editData.brandID          || '',
                bagsPerBox:       editData.bagsPerBox       || '',
                gperpacket:       editData.gperpacket       || '',
                seedsperpacket:   editData.seedsperpacket   || '',
                indSale:          editData.indSale          || 'NO',
                niche_category_id:editData.niche_category_id|| '',
                seedsID:          editData.seedsID          || '',
                marketplaces:     editData.marketplaces != null ? editData.marketplaces : ALL_MARKETPLACES,
            });
            setSelectedSeed(null);
            // Fetch sets that use this packet
            setLoadingSets(true);
            api.get(`/api/individual-packet-popup/${editData.SKU}/sets`)
                .then(r => setUsedInSets(r.data || []))
                .catch(() => setUsedInSets([]))
                .finally(() => setLoadingSets(false));
        } else {
            setForm({ SKU:'', packName:'', ASIN:'', FNSKU:'', GTIN:'', modelNumber:'', brandID:'',
                      bagsPerBox:'', gperpacket:'', seedsperpacket:'', indSale:'NO', niche_category_id:'',
                      seedsID:'', marketplaces: ALL_MARKETPLACES });
            setUsedInSets([]);
            setSelectedSeed(null);
        }
        setSeedSearch(''); setSeedDropOpen(false); setError('');
    }, [isOpen, editData]);

    // Resolve seed label once allSeeds loaded
    useEffect(() => {
        if (!editData?.seedsID || !allSeeds.length) return;
        const s = allSeeds.find(x => String(x.seedsID) === String(editData.seedsID));
        setSelectedSeed(s ? { seedsID: s.seedsID, label: s.combinedSeedsName || s.seedsName || `#${s.seedsID}` } : null);
    }, [editData, allSeeds]);

    // Close dropdown on outside click
    useEffect(() => {
        const h = (e) => {
            if (seedWrapRef.current && !seedWrapRef.current.contains(e.target))
                setSeedDropOpen(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    // Filtered seeds
    const filteredSeeds = useMemo(() => {
        if (!isOpen) return [];
        const q = seedSearch.trim().toLowerCase();
        return allSeeds.filter(s =>
            !q ||
            (s.combinedSeedsName || '').toLowerCase().includes(q) ||
            (s.seedsName         || '').toLowerCase().includes(q) ||
            (s.varietyName       || '').toLowerCase().includes(q)
        );
    }, [isOpen, allSeeds, seedSearch]);

    if (!isOpen) return null;

    const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

    const openDrop = () => {
        if (seedInputRef.current) {
            const r = seedInputRef.current.getBoundingClientRect();
            setDropStyle({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 320) });
        }
        setSeedDropOpen(true);
    };

    const selectSeed = (s) => {
        const label = s.combinedSeedsName || s.seedsName || `#${s.seedsID}`;
        setSelectedSeed({ seedsID: s.seedsID, label });
        setForm(p => ({ ...p, seedsID: s.seedsID }));
        setSeedSearch('');
        setSeedDropOpen(false);
    };

    const clearSeed = () => {
        setSelectedSeed(null);
        setForm(p => ({ ...p, seedsID: '' }));
        setSeedSearch('');
        setTimeout(() => seedInputRef.current?.focus(), 50);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.SKU.trim()) { setError('SKU is required'); return; }
        setSaving(true);
        try {
            if (editData) {
                await api.put(`/api/individual-packet-popup/${editData.SKU}`, form);
            } else {
                await api.post('/api/individual-packet-popup', form);
            }
            onSaved(); onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally { setSaving(false); }
    };

    return (
        <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="kit-popup wm-popup ip-popup">
                <div className="kit-popup__header">
                    <div>
                        <div className="kit-popup__title">
                            {editData ? `Edit — ${editData.packName || editData.SKU}` : 'Add Individual Packet'}
                        </div>
                        <div className="kit-popup__subtitle">
                            {editData ? `SKU: ${editData.SKU}` : 'Fill in packet details'}
                        </div>
                    </div>
                    <button className="kit-popup__close" type="button" onClick={onClose}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><use xlinkHref="#closeBtn" /></svg>
                    </button>
                </div>

                <div className="wm-popup__body">
                    <div className="wm-fields-grid">

                        <div className="kit-field">
                            <span className="kit-field__label">SKU *</span>
                            <input className="kit-field__input" value={form.SKU}
                                readOnly={!!editData}
                                onChange={setField('SKU')} placeholder="e.g. ORICPA_01_03"
                                style={editData ? { background:'#F8FAFC', color:'#64748B', cursor:'default', fontWeight:600 } : {}} />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Brand</span>
                            <select className="kit-field__input" value={form.brandID}
                                onChange={e => setForm(p => ({ ...p, brandID: e.target.value }))}>
                                <option value="">— Select brand —</option>
                                {brands.map(b => <option key={b.brandID} value={b.brandID}>{b.brandName}</option>)}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Pack Name</span>
                            <input className="kit-field__input" value={form.packName}
                                onChange={setField('packName')} placeholder="e.g. Carrot Parisian - 1G" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Niche</span>
                            <select className="kit-field__input" value={form.niche_category_id}
                                onChange={e => setForm(p => ({ ...p, niche_category_id: e.target.value }))}>
                                <option value="">— Select niche —</option>
                                {niches.map(n => <option key={n.niche_category_id} value={n.niche_category_id}>{n.niche_name}</option>)}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">ASIN</span>
                            <input className="kit-field__input" value={form.ASIN}
                                onChange={setField('ASIN')} placeholder="Amazon ASIN" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">FNSKU</span>
                            <input className="kit-field__input" value={form.FNSKU}
                                onChange={setField('FNSKU')} placeholder="Amazon FNSKU" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">GTIN</span>
                            <input className="kit-field__input" value={form.GTIN}
                                onChange={setField('GTIN')} placeholder="Barcode / GTIN" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Model Number</span>
                            <input className="kit-field__input" type="number" min="0" step="1"
                                value={form.modelNumber} onChange={setField('modelNumber')} placeholder="e.g. 273" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">G per Packet</span>
                            <input className="kit-field__input" type="number" min="0" step="0.001"
                                value={form.gperpacket} onChange={setField('gperpacket')} placeholder="e.g. 1.0" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Seeds per Packet</span>
                            <input className="kit-field__input" type="number" min="0"
                                value={form.seedsperpacket} onChange={setField('seedsperpacket')} placeholder="e.g. 200" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Bags per Box</span>
                            <input className="kit-field__input" type="number" min="1"
                                value={form.bagsPerBox} onChange={setField('bagsPerBox')} placeholder="e.g. 60" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Individual Sale</span>
                            <select className="kit-field__input" value={form.indSale}
                                onChange={e => setForm(p => ({ ...p, indSale: e.target.value }))}>
                                <option value="YES">Yes</option>
                                <option value="NO">No</option>
                            </select>
                        </div>

                        <MarketplacesField
                            value={form.marketplaces}
                            onChange={v => setForm(p => ({ ...p, marketplaces: v }))}
                        />

                        {editData && <ProductDetailsField productType="individualpackets" sku={editData.SKU} />}
                        {editData && <AlternateIdsField productType="individualpackets" sku={editData.SKU} />}

                    </div>

                    {/* Seeds binding — full width divider row */}
                    <div className="wm-seeds-section">
                        <div className="wm-seeds-label">
                            <i className="fas fa-leaf" />
                            Seeds
                        </div>
                        <div className="wm-seeds-row" ref={seedWrapRef}>
                            <div className="wm-seed-combo">
                                <i className="fas fa-search wm-seed-combo__icon" />
                                <input
                                    ref={seedInputRef}
                                    className={`wm-seed-combo__input${selectedSeed ? ' wm-seed-combo__input--selected' : ''}`}
                                    value={selectedSeed ? selectedSeed.label : seedSearch}
                                    readOnly={!!selectedSeed}
                                    placeholder="Search and select seed…"
                                    onChange={e => { setSeedSearch(e.target.value); setSeedDropOpen(true); }}
                                    onFocus={openDrop}
                                    onClick={() => { if (!selectedSeed) openDrop(); }}
                                />
                                {selectedSeed && (
                                    <button type="button" className="wm-seed-combo__clear"
                                        onClick={clearSeed} title="Clear seed">
                                        <i className="fas fa-times" />
                                    </button>
                                )}

                                {seedDropOpen && !selectedSeed && (
                                    <div className="set-comp__dropdown" style={dropStyle}>
                                        {filteredSeeds.length === 0 ? (
                                            <div className="set-comp__dropdown-empty">
                                                {seedSearch.trim() ? 'No seeds found' : 'Start typing to search…'}
                                            </div>
                                        ) : (
                                            filteredSeeds.slice(0, 100).map(s => (
                                                <div key={s.seedsID}
                                                    className="set-comp__dropdown-item"
                                                    onMouseDown={() => selectSeed(s)}>
                                                    <span className="set-comp__item-name">
                                                        {s.combinedSeedsName || s.seedsName}
                                                    </span>
                                                    <span className="set-comp__item-sku">#{s.seedsID}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Used in sets — edit mode only */}
                    {editData && (
                        <div className="ip-sets-section">
                            <div className="wm-seeds-label">
                                <i className="fas fa-boxes" />
                                Used in Sets
                            </div>
                            {loadingSets ? (
                                <span className="ip-sets-loading">
                                    <i className="fas fa-spinner fa-spin" /> Loading…
                                </span>
                            ) : usedInSets.length === 0 ? (
                                <span className="ip-sets-empty">Not included in any set</span>
                            ) : (
                                <div className="ip-sets-tags">
                                    {usedInSets.map(s => (
                                        <span key={s.SKU} className="ip-set-tag" title={s.SKU}>
                                            {s.brandName ? <span className="ip-set-brand">{s.brandName}</span> : null}
                                            {s.setmane}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="kit-popup__footer">
                    <span className="kit-popup__error">{error && `⚠ ${error}`}</span>
                    <button className="kit-popup__submit" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Saving…' : (editData ? 'Save Changes' : 'Create Packet')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default IndividualPacketPopup;
